import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AcsButton, AcsCard, AcsInput, AcsLogo, AcsPageShell, AcsStatusBadge } from '../components';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useI18n } from '../i18n/I18nProvider';
import { users as usersApi, type UserSummary } from '../api/client';

/**
 * Account del personale (§6.1, §10.3).
 *
 * Esiste per rendere raggiungibile il ruolo OPERATOR: finché l'unico modo di
 * creare un utente era la CLI `create-admin`, ogni persona dello staff
 * diventava amministratore, e la separazione fra chi legge e chi esporta
 * restava solo sulla carta.
 *
 * L'autorizzazione è del server: qui si rende visibile, non si applica.
 */
export function UsersPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const list = useQuery({ queryKey: ['users'], queryFn: usersApi.list });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'OPERATOR'>('OPERATOR');
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => usersApi.create(email, role),
    onSuccess: (created) => {
      setEmail('');
      setError(null);
      setNewPassword(created.initialPassword);
      refresh();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      usersApi.setActive(id, active),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const changeRole = useMutation({
    mutationFn: ({ id, next }: { id: string; next: 'ADMIN' | 'OPERATOR' }) =>
      usersApi.changeRole(id, next),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) => usersApi.resetPassword(id),
    onSuccess: (result) => {
      setError(null);
      setNewPassword(result.initialPassword);
    },
    onError: (cause: Error) => setError(cause.message),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    create.mutate();
  }

  return (
    <AcsPageShell
      header={
        <>
          <AcsLogo />
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--acs-space-4)' }}>
            <LanguageSwitcher />
            <Link to="/app" className="acs-button acs-button--secondary">
              {t.app.events}
            </Link>
          </div>
        </>
      }
    >
      <h1 style={{ fontSize: 'var(--acs-font-size-xl)' }}>{t.users.title}</h1>
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.users.hint}</p>

      <div className="acs-stack">
        <AcsCard>
          <h2 style={{ marginTop: 0 }}>{t.users.create}</h2>
          <form onSubmit={submit} className="acs-stack">
            <AcsInput
              label={t.users.emailLabel}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              required
            />

            <label className="acs-field">
              <span className="acs-field__label">{t.users.roleLabel}</span>
              <select
                className="acs-field__control"
                value={role}
                onChange={(e) => setRole(e.target.value as 'ADMIN' | 'OPERATOR')}
              >
                <option value="OPERATOR">{t.users.roleOperator}</option>
                <option value="ADMIN">{t.users.roleAdmin}</option>
              </select>
            </label>

            <div>
              <AcsButton type="submit" disabled={create.isPending}>
                {t.users.create}
              </AcsButton>
            </div>
          </form>

          {/*
            La password compare qui e da nessun'altra parte: nel database resta
            solo l'hash Argon2id. Va mostrata in modo che non passi inosservata.
          */}
          {newPassword ? (
            <div
              role="status"
              style={{
                marginTop: 'var(--acs-space-4)',
                padding: 'var(--acs-space-4)',
                borderRadius: 'var(--acs-radius-md)',
                background: 'var(--acs-color-decorative-soft)',
              }}
            >
              <strong>{t.users.created}</strong>
              <p style={{ margin: 'var(--acs-space-2) 0' }}>
                <code style={{ fontSize: 'var(--acs-font-size-lg)' }}>{newPassword}</code>
              </p>
              <p style={{ margin: 0, color: 'var(--acs-color-text-muted)' }}>
                {t.users.passwordOnce}
              </p>
            </div>
          ) : null}

          {error ? (
            <p className="acs-field__error" role="alert" style={{ marginTop: 'var(--acs-space-3)' }}>
              {error}
            </p>
          ) : null}
        </AcsCard>

        <AcsCard>
          <h2 style={{ marginTop: 0 }}>{t.users.title}</h2>
          <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.users.lastAdminHint}</p>

          {list.isLoading ? <p>{t.common.loading}</p> : null}
          {list.data?.users.length === 0 ? <p>{t.users.empty}</p> : null}

          <ul className="acs-stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {list.data?.users.map((user: UserSummary) => (
              <li
                key={user.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--acs-space-3)',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingBottom: 'var(--acs-space-3)',
                  borderBottom: '1px solid var(--acs-color-border)',
                }}
              >
                <span style={{ display: 'flex', gap: 'var(--acs-space-3)', alignItems: 'center' }}>
                  <strong>{user.email}</strong>
                  <AcsStatusBadge tone={user.active ? 'success' : 'neutral'}>
                    {user.active ? t.users.active : t.users.inactive}
                  </AcsStatusBadge>
                  <span style={{ color: 'var(--acs-color-text-muted)' }}>
                    {user.role === 'ADMIN' ? t.users.roleAdmin : t.users.roleOperator}
                  </span>
                </span>

                <span style={{ display: 'flex', gap: 'var(--acs-space-2)', flexWrap: 'wrap' }}>
                  <AcsButton
                    variant="secondary"
                    onClick={() =>
                      changeRole.mutate({
                        id: user.id,
                        next: user.role === 'ADMIN' ? 'OPERATOR' : 'ADMIN',
                      })
                    }
                  >
                    {user.role === 'ADMIN' ? t.users.makeOperator : t.users.makeAdmin}
                  </AcsButton>
                  <AcsButton variant="secondary" onClick={() => resetPassword.mutate(user.id)}>
                    {t.users.resetPassword}
                  </AcsButton>
                  <AcsButton
                    variant="secondary"
                    onClick={() => setActive.mutate({ id: user.id, active: !user.active })}
                  >
                    {user.active ? t.users.deactivate : t.users.activate}
                  </AcsButton>
                </span>
              </li>
            ))}
          </ul>
        </AcsCard>
      </div>
    </AcsPageShell>
  );
}
